import type { ComponentProps } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderBottomProps,
  BorderRadiusProps,
  BoxShadowProps,
  ColorProps,
  FontWeightProps,
} from 'styled-system';
import {
  backgroundColor,
  borderBottom,
  borderRadius,
  boxShadow,
  color,
  space,
  themeGet,
} from 'styled-system';

import type { BoxSizingProps, FlexGapProps } from 'common/theme/system-utilities';
import { boxSizing, flexGap } from 'common/theme/system-utilities';
import { Element } from 'common/types/tracking';
import BaseNavigationLink from './BaseNavigationLink';
import EntryPointIcon from './common/EntryPointIcon';
import Icon from './common/Icon';
import { SideNavPlacement, trackNavigationClick } from './tracking';
import type { NavigationIconData } from './utils';

const ICON_SIZE = 32;

interface NavigationLinkProps {
  label: string;
  itemIndex: number;
  itemsCount: number;
  icon?: NavigationIconData;
  onClick?: () => void;
}

const UnstyledLink = styled(BaseNavigationLink)<
  BackgroundColorProps &
    BorderBottomProps &
    BorderRadiusProps &
    BoxShadowProps &
    ColorProps &
    BoxSizingProps &
    FontWeightProps &
    FlexGapProps
>`
  ${backgroundColor};
  ${borderRadius};
  ${boxShadow};
  ${boxSizing};
  ${color};
  ${space};
  ${flexGap};

  &:not(:last-of-type) {
    ${borderBottom};
  }

  ${props => css`
    &:hover,
    &:active {
      color: ${themeGet('colors.sys.neutral.text.active')(props)};

      img {
        filter: invert(1);
      }

      svg {
        fill: ${themeGet('colors.sys.neutral.text.active')(props)};
      }
    }
  `}
`;

const Link = (props: ComponentProps<typeof UnstyledLink>) => (
  <UnstyledLink
    backgroundColor="sys.neutral.background.strong"
    borderBottom="none"
    borderRadius="medium"
    boxShadow="none"
    boxSizing="content-box"
    color="sys.neutral.text.inverted"
    fontWeight="normal"
    justifyContent="flex-start"
    gap="sp_12"
    py="sp_8"
    pl="sp_4"
    tabIndex={0}
    {...props}
  />
);

const BottomNavigationLink = ({
  label,
  icon,
  itemIndex,
  itemsCount,
  onClick,
  ...props
}: NavigationLinkProps) => {
  const handleClick = () => {
    onClick?.();

    trackNavigationClick({
      element: Element.Link,
      label,
      placement: SideNavPlacement.Courtesy,
      position: itemIndex,
      length: itemsCount,
    });
  };

  return (
    <Link {...props} onClick={handleClick}>
      {icon?.type === 'img' && <EntryPointIcon src={icon.src} alt="" />}
      {icon?.type === 'icon' && (
        <Icon name={icon.name} size={ICON_SIZE} fill="sys.neutral.text.inverted" />
      )}
      <span dangerouslySetInnerHTML={{ __html: label }} />
    </Link>
  );
};

export default BottomNavigationLink;
